/**
 * Counted marks that would be drawn on top of one another, made into one.
 *
 * Two kinds of mark on this map carry a number in a disc: the winery layer's
 * chips, which the server bins on a grid of degrees, and the reader's own
 * tastings. A grid knows nothing about a screen. Southern California's 467
 * producers straddle a cell edge at the 120th meridian, so the opening view of
 * the world drew "156" and "311" as two chips fifteen pixels apart, each over
 * the other's numeral, and neither could be read — on the first screen the app
 * shows. Wherever two cells' members cluster near their shared edge the same
 * thing happens at any zoom.
 *
 * So before a set of counted marks is drawn, any two whose DISCS would touch
 * at the zoom they were made for are merged: one mark, at the weighted middle
 * of the two, carrying the sum. The sum is still true — it is the same
 * producers — and one legible "467" says more than two numbers nobody can
 * read.
 *
 * WHAT IT COSTS, AND WHEN. It runs once per set of marks, when the set
 * arrives — never per frame and never per pointer move. The marks are the
 * server's cells (at most 400, in practice a few dozen) or the places a reader
 * has tasted in. Each pass is a comparison of every mark with every mark kept
 * so far, on positions worked out once, and it is done before the source is
 * handed anything. Timed in Node on a busy machine: under 0.1 ms for 40 marks,
 * about half a millisecond for 150, and 0.8 to 2.5 ms for the full 400 —
 * the most when nothing merges, since every mark is then compared with every
 * other.
 *
 * AND WHAT IT DOES NOT FOLLOW. The answer is right for the zoom it was worked
 * out at. A reader who zooms before the next set arrives sees these marks
 * drift together or apart for the length of the gesture, exactly as the
 * server's own cells already did; the set for the zoom they stop at replaces
 * them.
 *
 * No import from maplibre-gl: the projection is four lines of Web Mercator,
 * and this file has to be testable without a map.
 */

export interface CountedMark {
  lng: number
  lat: number
  count: number
}

/** A disc already on the map that another mark has to keep off: where, and how big it is drawn. */
export interface Obstacle {
  lng: number
  lat: number
  radius: number
}

// MapLibre's world is 512 px wide at zoom 0.
const WORLD_PX = 512

const worldSize = (zoom: number) => WORLD_PX * 2 ** zoom

/** Where a point is on the Web Mercator plane at a zoom, in screen pixels. */
export function mercatorPx(lng: number, lat: number, zoom: number): [number, number] {
  const size = worldSize(zoom)
  const clamped = Math.max(-85.0511, Math.min(85.0511, lat))
  const y = 0.5 - Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360)) / (2 * Math.PI)

  return [((lng + 180) / 360) * size, y * size]
}

/** And back: the point a pixel on that plane is. */
export function fromMercatorPx(x: number, y: number, zoom: number): [number, number] {
  const size = worldSize(zoom)
  const lat = (Math.atan(Math.exp((0.5 - y / size) * 2 * Math.PI)) * 360) / Math.PI - 90

  return [(x / size) * 360 - 180, lat]
}

/**
 * How far apart two points are on screen at a zoom, the short way round the
 * world: a mark at 179°E and one at 179°W are neighbours.
 */
export function pxBetween(first: CountedMark, second: CountedMark, zoom: number): number {
  const [x1, y1] = mercatorPx(first.lng, first.lat, zoom)
  const [x2, y2] = mercatorPx(second.lng, second.lat, zoom)
  const across = Math.abs(x1 - x2)

  return Math.hypot(Math.min(across, worldSize(zoom) - across), y1 - y2)
}

/**
 * The marks, with any that would overlap made one.
 *
 * `radius` is how big a mark of a count is DRAWN, in pixels — the same numbers
 * the layer's paint uses, or the merge would be for discs of another size —
 * and `gap` is the clear ground two marks keep between them.
 *
 * The largest first, so a small mark joins the big one beside it rather than
 * the other way round, and the result does not depend on the order the server
 * happened to send the cells in. A merged mark is bigger than either part, so
 * it may now touch a third; the pass is repeated until one changes nothing,
 * which for marks on a grid is the second.
 *
 * A mark may carry more than its three numbers — the tastings it stands for,
 * say. Whatever it carries rides along on the copy that is kept, and `absorb`
 * is told each time another mark is folded into it, so the caller decides what
 * the union of two payloads is.
 *
 * Returns the array it was given when nothing overlapped, so a caller can tell
 * "unchanged" by identity.
 */
export function mergeCrowded<Mark extends CountedMark>(
  marks: readonly Mark[],
  zoom: number,
  radius: (count: number) => number,
  gap = 2,
  absorb?: (into: Mark, other: Mark) => void,
): readonly Mark[] {
  if (marks.length < 2) return marks

  const size = worldSize(zoom)

  // Each mark is put on the plane ONCE, with the radius its count is drawn at,
  // and every comparison after that is a subtraction and a square root. Asking
  // `pxBetween` for each pair projected both ends again every time — a
  // logarithm and a tangent per mark per pair — which for the server's full
  // 400 cells was most of the cost of the merge.
  interface Placed {
    mark: Mark
    x: number
    y: number
    reach: number
  }
  const place = (mark: Mark): Placed => {
    const [x, y] = mercatorPx(mark.lng, mark.lat, zoom)

    return { mark, x, y, reach: radius(mark.count) }
  }
  const largestFirst = (first: Placed, second: Placed) => second.mark.count - first.mark.count

  let current = marks.map(place).sort(largestFirst)
  let merged = false

  // A set of marks on a grid settles in two passes. The bound is for a set
  // that is not on one: it must end, and a mark too many is not worth a hang.
  for (let pass = 0; pass < 8; pass++) {
    const kept: Placed[] = []
    let changed = false

    for (const entry of current) {
      let into: Placed | undefined

      for (const other of kept) {
        const across = Math.abs(other.x - entry.x)
        // The short way round the world, as `pxBetween` measures it.
        const apart = Math.hypot(Math.min(across, size - across), other.y - entry.y)

        if (apart < other.reach + entry.reach + gap) {
          into = other
          break
        }
      }

      if (!into) {
        // A copy the first time a mark is kept: the caller's marks are never
        // written to.
        kept.push(pass === 0 ? { ...entry, mark: { ...entry.mark } } : entry)
        continue
      }

      const total = into.mark.count + entry.mark.count

      // The weighted middle, on the plane the marks are drawn on, so a merged
      // mark sits where the eye would put it between the two. Two marks either
      // side of the antimeridian are a few pixels apart and a world apart in
      // `x`; the second is brought round to the first's side.
      const x2 = entry.x - into.x > size / 2 ? entry.x - size : into.x - entry.x > size / 2 ? entry.x + size : entry.x
      const x = (into.x * into.mark.count + x2 * entry.mark.count) / total
      const y = (into.y * into.mark.count + entry.y * entry.mark.count) / total
      const [around, lat] = fromMercatorPx(x, y, zoom)

      absorb?.(into.mark, entry.mark)
      into.mark.lng = around < -180 ? around + 360 : around > 180 ? around - 360 : around
      into.mark.lat = lat
      into.mark.count = total
      // Back onto the plane's own span, so the next comparison's "short way
      // round" is measured from where the mark now is.
      into.x = x < 0 ? x + size : x >= size ? x - size : x
      into.y = y
      into.reach = radius(total)
      changed = true
    }

    if (!changed) break

    merged = true
    current = kept.sort(largestFirst)
  }

  return merged ? current.map((entry) => entry.mark) : marks
}

/**
 * A mark moved clear of the fixed marks it would be drawn on top of.
 *
 * The other half of the same problem. The winery chips and the reader's own
 * tastings are two sets of counted discs, and they collect in the same places
 * — a reader tastes where the wineries are — so a tasting count used to be
 * painted squarely over the winery chip beside it, and the chip's number could
 * not be read. The two cannot be merged: they count different things. So the
 * fixed set stays where it is and the mark steps aside.
 *
 * WHERE TO. Round each disc it lies on there are eight places it could stand,
 * touching that disc with `gap` between them: the one straight out along the
 * line between their centres, and seven more at eighths of a turn from it. The
 * mark takes the NEAREST of all of those to where it was that is clear of
 * every fixed disc. Straight out is the nearest, so that is where it goes
 * unless a second chip is standing there — which is the case this is written
 * for: a tasting between two chips that pushing away from one walks into the
 * other. A mark exactly on a chip has no "straight out", and its first place
 * is up and to the right, where a badge on a chip is looked for.
 *
 * If no place is clear — three chips in a knot — it takes the straight-out
 * place from the first chip and overlaps what it must. That is not worth a
 * solver.
 */
export function clearOf(
  mark: CountedMark,
  markRadius: number,
  fixed: readonly Obstacle[],
  zoom: number,
  gap = 3,
): [number, number] {
  const [x, y] = mercatorPx(mark.lng, mark.lat, zoom)
  const discs = fixed.map((other) => {
    const [ox, oy] = mercatorPx(other.lng, other.lat, zoom)

    return { x: ox, y: oy, least: other.radius + markRadius + gap }
  })
  const clear = (px: number, py: number) => discs.every((disc) => Math.hypot(px - disc.x, py - disc.y) >= disc.least - 0.01)

  if (clear(x, y)) return [mark.lng, mark.lat]

  let best: [number, number] | null = null
  let bestApart = Infinity
  let fallback: [number, number] | null = null

  for (const disc of discs) {
    const dx = x - disc.x
    const dy = y - disc.y
    const apart = Math.hypot(dx, dy)
    if (apart >= disc.least) continue

    const out = apart < 0.5 ? -Math.PI / 4 : Math.atan2(dy, dx)

    for (let step = 0; step < 8; step++) {
      // Out, then alternately to either side of out, a little further round
      // each time: 0, +45, -45, +90, -90 … degrees.
      const turn = (Math.ceil(step / 2) * (step % 2 === 1 ? 1 : -1) * Math.PI) / 4
      const px = disc.x + Math.cos(out + turn) * disc.least
      const py = disc.y + Math.sin(out + turn) * disc.least

      fallback ??= [px, py]
      if (!clear(px, py)) continue

      // Nearer by a real margin: places that are equally near are taken in
      // the order they are tried, so "straight out" wins a tie it should win.
      const moved = Math.hypot(px - x, py - y)
      if (moved >= bestApart - 0.01) continue

      best = [px, py]
      bestApart = moved
    }
  }

  const [px, py] = best ?? fallback ?? [x, y]

  return fromMercatorPx(px, py, zoom)
}
